import { A } from '@adornis/base/env-info.js';
import { logger } from '@adornis/base/logging.js';
import { defineMailTemplate } from '@adornis/mails/db/mail-template-definition.js';
import { Recipient } from '@adornis/mails/db/recipients.js';
import { prepareMessage } from '@adornis/mails/server/communication.js';
import { getUserByID } from '../api/users.js';
import { getMailTemplateForInstitution } from '../api/mail-templates.js';
import { InstitutionMailTemplate, MailTemplateType, MailTemplateVars } from '../entities/mail-templates.js';
import { UserNotFoundError } from '../errors/UserNotFoundError.js';
import { globalNotification } from '../GobalNotification.js';
import { assembleInstitutionMail } from '../mails/assemble-mail.js';
import { mailer } from '../mails/communication.js';
import { institutionMailHint, institutionMailVariables } from '../mails/mail-template-catalog.js';

defineMailTemplate(MailTemplateType.CHANGE_EMAIL, {
  getMailData: arg1 => ({ data: {}, attachments: {}, scope: 'default' }),
  defaultTemplate: {
    subject: 'Email Änderung bestätigen',
    content: `<p>Hallo {{title}} {{firstname}} {{lastname}},</p>
<p>Sie haben angeforder ihre E-Mail Adresse zu &auml;ndern. Falls nicht, können Sie diese E-Mail ignorieren.</p>
<p>Klicken Sie auf den folgenden Link um die Änderung zu best&auml;tigen: {{change-email-link}}</p>
<p>Viele Gr&uuml;&szlig;e</p>
<p>------------------------------------------------------------------</p>
<p>Hello {{title}} {{firstname}} {{lastname}},</p>
<p>You have requested to change your e-mail address. If not, please ignore this e-mail.</p>
<p>Please click on the following link to confirm the change: {{change-email-link}}</p>
<p>Best regards</p>`,
    footer: '',
  },
  variables: institutionMailVariables(MailTemplateType.CHANGE_EMAIL),
  attachments: [],
  displayName: 'Email Änderung bestätigen',
  editorHint: () => institutionMailHint(MailTemplateType.CHANGE_EMAIL),
});

export async function notifyEmailChangeConfirmation(userID: string, newEmail: string, token: string): Promise<void> {
  const user = await getUserByID(userID)({
    _id: 1,
    _class: 1,
    title: 1,
    firstName: 1,
    lastName: 1,
  });
  if (!user) throw new UserNotFoundError(userID);

  const mailTemplate = await getMailTemplateForInstitution(
    undefined,
    MailTemplateType.CHANGE_EMAIL,
  )(InstitutionMailTemplate.allFields);

  const emailChangeLink = A.absoluteUrl(
    `/change-email?userID=${encodeURIComponent(userID)}&token=${encodeURIComponent(token)}`,
  );

  const { content: mailContent } = await assembleInstitutionMail({
    template: mailTemplate,
    infos: {
      [MailTemplateVars.TITLE]: user.title ?? '',
      [MailTemplateVars.FIRST_NAME]: InstitutionMailTemplate.mapFirstNameVar(user),
      [MailTemplateVars.LAST_NAME]: InstitutionMailTemplate.mapLastNameVar(user),
      [MailTemplateVars.CHANGE_EMAIL_LINK]: `<a href="${emailChangeLink}" target="_blank">Email Änderung bestätigen</a>`,
    } as any,
  });

  if (!mailContent) throw new Error("mail template couldn't be found.");

  const recipient = new Recipient(newEmail, undefined, undefined, undefined);
  const message = await prepareMessage({
    subject: mailTemplate.subject,
    html: mailContent,
    attachments: [],
  });

  try {
    const _mailer = await mailer;
    if (!_mailer) throw new Error('could not get mailer');
    await _mailer.sendMail(message, recipient);
  } catch (err) {
    const translationKey = (err as any)?.extensions?.translationKey;
    if (translationKey === 'send_mail_rejected') {
      globalNotification.next(
        'Die E-Mail konnte nicht versendet werden, da die Empfänger oder Absender-Adresse derzeit blockiert ist.',
      );
    }
    logger.error(
      { newEmail, errorName: err instanceof Error ? err.name : undefined },
      'E-Mail Konnte nicht verschickt werden; 8373',
    );
  }
}
